import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameColumnDef, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import findColumn from 'data-frames/util/findColumn';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import convertDataFrameToDeprecatedDataFrame from 'data-frames/util/legacy/convertDataFrameToDeprecatedDataFrame';
import validateColumnType from 'data-frames/util/validateColumnType';
import pick from 'util/pick';
import {JSONValue} from 'util/Types';

import {ColumnInfo} from './PropConverterTypes';

export function makeChartFriendlyDataFrame(dataFrame: DataFrame, columnInfos: Array<ColumnInfo>): DataFrame {
  const systemColumns = dataFrame.columns.filter((column) => isSystemColumn(column));

  const newColumns = (
    columnInfos.map((columnInfo) => {
      const {chartDataColumnName, inputDataColumnSystemName, typeValidationOptions} = columnInfo;
      const columnDef = findColumn(dataFrame, inputDataColumnSystemName)!;

      return {
        ...columnDef,
        type: {
          ...columnDef.type,
          leafType: validateColumnType(columnDef.type, typeValidationOptions),
        },
        systemName: chartDataColumnName,
      };
    }) as Array<DataFrameColumnDef>
  ).concat(systemColumns);

  const newRows = dataFrame.rows.map((row) => {
    const baseRow = pick(row, ...Object.values(SystemColumnName));

    return columnInfos.reduce((rowAccumulator, columnInfo) => {
      const {chartDataColumnName: newSystemName, inputDataColumnSystemName} = columnInfo;

      return {
        ...rowAccumulator,
        [newSystemName]: row[inputDataColumnSystemName],
      };
    }, baseRow);
  });

  return {
    ...dataFrame,
    columns: newColumns,
    rows: newRows,
  };
}

/**
 * Return a `DeprecatedDataFrame` generated from a `DataFrame` and `ColumnInfo`s that has the same data as the
 * legacy functionality where we converted `RunQueryOutputLegacyData` to `DeprecatedDataFrame`.
 *
 * We will first generate a `DataFrame` with the followings:
 *  - One column for each of the `columnInfos`, those columns will have the following
 *      - The `systemName` is `columnInfos`'s `chartDataColumnName`
 *      - The `type` has the `leafType` returned by validating the `DataFrame`'s `column`'s `type` against `columnInfos`'s `typeValidationOptions`
 *      - The display name is unchanged
 *  - All system columns are additionally included
 *  - The columns are in the order of the `columnInfos`
 *
 * @deprecated This function is only used for charts that use a version of `DeprecatedDataFrame` as their input.
 * All new charts should be made with a `DataFrame`, so they should not use this function.
 */
// TODO(BNCH-24546): Remove `convertDataFrameToLegacyChartData` once no chart uses a `DeprecatedDataFrame` anymore
export default function convertDataFrameToLegacyChartData(
  dataFrame: DataFrame,
  columnInfos: Array<ColumnInfo>
): DeprecatedDataFrame<Record<string, JSONValue>> {
  const chartFriendlyDataFrame = makeChartFriendlyDataFrame(dataFrame, columnInfos);
  return convertDataFrameToDeprecatedDataFrame(chartFriendlyDataFrame);
}
